import { useEffect, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { WifiOff, RefreshCw, Phone, Zap } from 'lucide-react'
import env from '@utils/env'

const FUNNY_LINES = [
  "Subho broke the server again. Classic.",
  "Subho is probably napping. Give it a sec.",
  "Subho forgot to pay the server bill. As usual.",
  "The server ghost-read Subho's last request.",
  "Subho deployed on a Friday. We don't talk about that.",
  "API is on a tea break. Subho's idea, obviously.",
  "Subho said 'it works on my machine'. The machine disagrees.",
  "404: Subho's attention not found.",
  "The hamsters powering the server need a break.",
  "Subho is in a meeting about fixing servers instead of fixing servers.",
]

const RETRY_INTERVALS = [3, 6, 10, 15, 20]

export const API_ERROR_EVENT = 'api:server_down'
export const API_OK_EVENT    = 'api:server_up'

const ApiErrorBanner = () => {
  const [visible,      setVisible]      = useState(false)
  const [countdown,    setCountdown]    = useState(0)
  const [attempt,      setAttempt]      = useState(0)
  const [retrying,     setRetrying]     = useState(false)
  const [funnyIdx,     setFunnyIdx]     = useState(0)
  const [resolved,     setResolved]     = useState(false)
  const nextFunny = useCallback(() => {
    setFunnyIdx(i => (i + 1) % FUNNY_LINES.length)
  }, [])

  useEffect(() => {
    const show = () => {
      setVisible(true)
      setResolved(false)
      setFunnyIdx(Math.floor(Math.random() * FUNNY_LINES.length))
    }
    const hide = () => {
      setResolved(true)
      setTimeout(() => setVisible(false), 2200)
    }
    window.addEventListener(API_ERROR_EVENT, show)
    window.addEventListener(API_OK_EVENT,    hide)
    return () => {
      window.removeEventListener(API_ERROR_EVENT, show)
      window.removeEventListener(API_OK_EVENT,    hide)
    }
  }, [])

  useEffect(() => {
    if (!visible || resolved) return
    const delay = RETRY_INTERVALS[Math.min(attempt, RETRY_INTERVALS.length - 1)]
    setCountdown(delay)
    const tick = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(tick); return 0 }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(tick)
  }, [visible, attempt, resolved])

  useEffect(() => {
    if (countdown !== 0 || !visible || resolved) return
    setRetrying(true)
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('api:retry'))
      setAttempt(a => a + 1)
      setRetrying(false)
    }, 600)
    return () => clearTimeout(timer)
  }, [countdown, visible, resolved])

  useEffect(() => {
    if (!visible) return
    const id = setInterval(nextFunny, 4500)
    return () => clearInterval(id)
  }, [visible, nextFunny])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="api-error-banner"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999] w-full max-w-sm sm:max-w-md px-3"
        >
          <div className={`rounded-2xl shadow-2xl border overflow-hidden transition-colors duration-500
            ${resolved
              ? 'bg-green-900/95 border-green-700/60'
              : 'bg-gray-950/97 border-red-800/40'
            }`}>

            {/* top strip */}
            <div className={`h-1 w-full transition-colors duration-500 ${resolved ? 'bg-green-500' : 'bg-red-500'}`}>
              {!resolved && countdown > 0 && (
                <motion.div
                  className="h-full bg-orange-400"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: RETRY_INTERVALS[Math.min(attempt, RETRY_INTERVALS.length - 1)], ease: 'linear' }}
                  key={attempt}
                />
              )}
            </div>

            <div className="p-4">
              {resolved ? (
                <motion.div
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-xl bg-green-500/20 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-green-300">Back online!</p>
                    <p className="text-xs text-green-500 mt-0.5">Subho woke up. All systems go.</p>
                  </div>
                </motion.div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0 mt-0.5">
                    {retrying
                      ? <RefreshCw className="w-4 h-4 text-orange-400 animate-spin" />
                      : <WifiOff className="w-4 h-4 text-red-400" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-white">
                        {retrying ? 'Reconnecting…' : 'Server Unreachable'}
                      </p>
                      {!retrying && countdown > 0 && (
                        <span className="text-xs font-mono text-gray-400 shrink-0">retry in {countdown}s</span>
                      )}
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.p
                        key={funnyIdx}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.3 }}
                        className="text-xs text-gray-400 mt-0.5 italic"
                      >
                        "{FUNNY_LINES[funnyIdx]}"
                      </motion.p>
                    </AnimatePresence>

                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => {
                          setAttempt(a => a + 1)
                          setCountdown(0)
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" /> Retry Now
                      </button>
                      <a
                        href={`https://wa.me/${env.whatsappNumber}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-400 text-xs font-semibold transition-colors"
                      >
                        <Phone className="w-3 h-3" /> Poke Subho
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ApiErrorBanner
